import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  InputNumber,
  Select,
  Button,
  Tag,
  DatePicker,
  Popconfirm,
  Tooltip,
  Radio
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { DeleteOutlined, PlusOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const WEEKDAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const weekdayLabel = (v) => WEEKDAYS.find((d) => d.value === v)?.label || `Day ${v}`;

/**
 * Editor for EventType.dailyCapacityRules — lets an admin opt specific dates or
 * recurring weekdays into MULTIPLE concurrent bookings. When no rule matches a
 * date, the default single-appointment behavior applies.
 */
const DailyCapacitySection = ({ dailyCapacityRules = [], onChange }) => {
  const [rules, setRules] = useState(dailyCapacityRules || []);

  // New-rule / edit form state
  const [scope, setScope] = useState('date');
  const [date, setDate] = useState(null);
  const [dayOfWeek, setDayOfWeek] = useState(null);
  const [maxConcurrent, setMaxConcurrent] = useState(2);
  const [maxPerDay, setMaxPerDay] = useState(null);
  // null when adding a new rule; the row index when editing an existing rule.
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => {
    setRules(dailyCapacityRules || []);
  }, [dailyCapacityRules]);

  const commit = (next) => {
    setRules(next);
    onChange?.(next);
  };

  const resetForm = () => {
    setScope('date');
    setDate(null);
    setDayOfWeek(null);
    setMaxConcurrent(2);
    setMaxPerDay(null);
    setEditingIndex(null);
  };

  const startEdit = (index) => {
    const r = rules[index];
    if (!r) return;
    setScope(r.scope);
    setDate(r.scope === 'date' && r.date ? dayjs(r.date) : null);
    setDayOfWeek(r.scope === 'dayOfWeek' ? r.dayOfWeek : null);
    setMaxConcurrent(r.maxConcurrentBookings || 2);
    setMaxPerDay(r.maxBookingsPerDay || null);
    setEditingIndex(index);
  };

  const saveRule = () => {
    if (scope === 'date' && !date) return;
    if (scope === 'dayOfWeek' && dayOfWeek === null) return;

    const rule = {
      scope,
      date: scope === 'date' ? date.startOf('day').toISOString() : null,
      dayOfWeek: scope === 'dayOfWeek' ? dayOfWeek : null,
      maxConcurrentBookings: maxConcurrent || 2,
      maxBookingsPerDay: maxPerDay || null,
      isEnabled: editingIndex !== null ? (rules[editingIndex]?.isEnabled !== false) : true
    };

    // Prevent obvious duplicates (same scope + target), ignoring the row being edited.
    const isDup = rules.some((r, i) =>
      i !== editingIndex &&
      r.scope === rule.scope &&
      (rule.scope === 'date'
        ? dayjs(r.date).isSame(dayjs(rule.date), 'day')
        : r.dayOfWeek === rule.dayOfWeek)
    );
    if (isDup) return;

    if (editingIndex !== null) {
      commit(rules.map((r, i) => (i === editingIndex ? rule : r)));
    } else {
      commit([...rules, rule]);
    }
    resetForm();
  };

  const removeRule = (index) => {
    if (editingIndex === index) resetForm();
    commit(rules.filter((_, i) => i !== index));
  };

  const describe = (r) =>
    r.scope === 'date'
      ? dayjs(r.date).format('MMM D, YYYY')
      : `Every ${weekdayLabel(r.dayOfWeek)}`;

  // Display order: specific dates first (chronological), then recurring
  // weekdays (Sun→Sat). Each entry keeps its original index so edit/delete
  // still target the correct underlying rule.
  const sortedRules = rules
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      if (a.r.scope !== b.r.scope) return a.r.scope === 'date' ? -1 : 1;
      if (a.r.scope === 'date') return dayjs(a.r.date).valueOf() - dayjs(b.r.date).valueOf();
      return (a.r.dayOfWeek ?? 0) - (b.r.dayOfWeek ?? 0);
    });

  return (
    <div style={{
      marginTop: '24px', padding: '20px', background: '#f6ffed',
      borderRadius: '8px', border: '1px solid #b7eb8f'
    }}>
      <h4 style={{ marginBottom: '8px', color: '#1a1a1a', fontSize: '16px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <FontAwesomeIcon icon={faUsers} style={{ color: '#52c41a' }} />
        Multiple Bookings Per Day
        <Tooltip
          title="Choose specific dates or recurring weekdays where more than one booking is allowed at the same time. On these days, up to the set number of bookings may overlap. All other days keep the default single-booking behavior."
          placement="right"
        >
          <FontAwesomeIcon icon={faQuestionCircle} style={{ color: '#8c8c8c', cursor: 'help', fontSize: '14px' }} />
        </Tooltip>
      </h4>
      <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#666' }}>
        By default only one booking is allowed per time window. Add a rule to permit multiple concurrent bookings on the dates or weekdays you choose.
      </p>

      {/* Existing rules */}
      {rules.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          {sortedRules.map(({ r, i }) => (
            <div
              key={`${r.scope}-${r.date || r.dayOfWeek}-${i}`}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', marginBottom: '8px',
                background: editingIndex === i ? '#f6ffed' : '#fff',
                border: editingIndex === i ? '1px solid #52c41a' : '1px solid #d9f7be',
                borderRadius: '6px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <Tag color={r.scope === 'date' ? 'blue' : 'green'}>
                  {r.scope === 'date' ? 'Date' : 'Weekday'}
                </Tag>
                <strong>{describe(r)}</strong>
                <span style={{ color: '#595959' }}>
                  up to <strong>{r.maxConcurrentBookings}</strong> at once
                  {r.maxBookingsPerDay ? `, max ${r.maxBookingsPerDay}/day` : ''}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Tooltip title="Edit this rule">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => startEdit(i)}
                  />
                </Tooltip>
                <Popconfirm title="Remove this rule?" onConfirm={() => removeRule(i)} okText="Remove" cancelText="Cancel">
                  <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / edit rule */}
      <div style={{ padding: '12px', background: '#fff', border: '1px dashed #b7eb8f', borderRadius: '6px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#389e0d', marginBottom: '10px' }}>
          {editingIndex !== null ? 'Edit rule' : 'Add a rule'}
        </div>
        <Radio.Group
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          style={{ marginBottom: '12px' }}
        >
          <Radio.Button value="date">Specific date</Radio.Button>
          <Radio.Button value="dayOfWeek">Day of week</Radio.Button>
        </Radio.Group>

        <Row gutter={12} align="bottom">
          <Col span={8}>
            {scope === 'date' ? (
              <>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Date</div>
                <DatePicker
                  value={date}
                  onChange={setDate}
                  style={{ width: '100%' }}
                  format="MMM D, YYYY"
                  disabledDate={(d) => d && d < dayjs().startOf('day')}
                />
              </>
            ) : (
              <>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Weekday</div>
                <Select
                  value={dayOfWeek}
                  onChange={setDayOfWeek}
                  style={{ width: '100%' }}
                  placeholder="Select day"
                  options={WEEKDAYS}
                />
              </>
            )}
          </Col>
          <Col span={7}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Max at same time</div>
            <InputNumber min={1} max={100} value={maxConcurrent} onChange={setMaxConcurrent} style={{ width: '100%' }} />
          </Col>
          <Col span={6}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Max per day (optional)</div>
            <InputNumber min={1} max={500} value={maxPerDay} onChange={setMaxPerDay} style={{ width: '100%' }} placeholder="Default" />
          </Col>
          <Col span={3}>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Tooltip title={editingIndex !== null ? 'Save changes' : 'Add rule'}>
                <Button
                  type="primary"
                  icon={editingIndex !== null ? <CheckOutlined /> : <PlusOutlined />}
                  onClick={saveRule}
                  style={{ flex: 1 }}
                />
              </Tooltip>
              {editingIndex !== null && (
                <Tooltip title="Cancel">
                  <Button icon={<CloseOutlined />} onClick={resetForm} />
                </Tooltip>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DailyCapacitySection;
